/**
 * Utility functions for handling resume files
 */

export const initResumeMetadataObjects = (values) => {
	if (!values.resume_files_is_default) values.resume_files_is_default = {};
	if (!values.resume_files_id) values.resume_files_id = {};
	if (!values.resume_files_is_remove) values.resume_files_is_remove = {};
};

export const shouldBeDefaultFile = (fileCount, isDefault) =>
	fileCount === 1 || isDefault === "1";

export const getNonRemovedIndices = (files, isRemoveObj) => {
	const indices = [];
	files.forEach((file, idx) => {
		const isRemove =
			file &&
			(file.is_remove === "1" ||
				file._ui_removed === true ||
				isRemoveObj?.[idx] === "1");
		if (!isRemove) indices.push(idx);
	});
	return indices;
};

export const getResumeFileId = (file) =>
	file instanceof File ? null : extractResumeFileId(file);

export const reindexResumeMetadataObjects = (
	index,
	updatedResumes,
	updatedIsDefault,
	updatedIds,
	updatedIsRemove
) => {
	for (let i = index; i < updatedResumes.length; i++) {
		if (updatedIsDefault[i + 1] !== undefined)
			updatedIsDefault[i] = updatedIsDefault[i + 1];
		if (updatedIds[i + 1] !== undefined) updatedIds[i] = updatedIds[i + 1];
		if (updatedIsRemove[i + 1] !== undefined)
			updatedIsRemove[i] = updatedIsRemove[i + 1];
	}
	delete updatedIsDefault[updatedResumes.length];
	delete updatedIds[updatedResumes.length];
	delete updatedIsRemove[updatedResumes.length];
};

/**
 * Extract ID from various resume file object formats
 * @param {Object|File} fileObj - Resume file object in various formats
 * @returns {string|number|null} - The extracted ID or null if not found
 */
export const extractResumeFileId = (fileObj) => {
	if (!fileObj) return null;

	// Handle our custom _isFile wrapper
	if (fileObj._isFile && fileObj._file) {
		// File objects don't have IDs
		return null;
	}

	// Direct ID property
	if (fileObj.id) return fileObj.id;

	// ID in resume_file object
	if (fileObj.resume_file && fileObj.resume_file.id)
		return fileObj.resume_file.id;

	return null;
};

/**
 * Convert boolean or numeric is_default values to consistent string format "0" or "1"
 * @param {any} value - The is_default value in any format
 * @returns {string} - "1" for true/1/"1"/"true", "0" for false/0/"0"/"false"
 */
export const normalizeDefaultValue = (value) => {
	if (value === true || value === "true" || value === 1 || value === "1") {
		return "1";
	}
	return "0";
};

/**
 * Check if a resume file array has any default file
 * @param {Array} files - Array of resume files
 * @returns {boolean} - True if any file is marked as default
 */
export const hasDefaultFile = (files) => {
	if (!Array.isArray(files) || files.length === 0) return false;

	return files.some((item) => {
		if (!item) return false;

		if (typeof item === "object") {
			return (
				item.is_default === true ||
				item.is_default === "true" ||
				item.is_default === 1 ||
				item.is_default === "1"
			);
		}
		return false;
	});
};

/**
 * Process resume files into a consistent format for form handling
 * @param {Array|Object} resumeData - The resume data from API or other sources
 * @returns {Object} - Object containing formatted files and metadata objects
 */
export const processResumeFiles = (resumeData) => {
	let resumeFiles = [];
	let resumeFilesIsDefault = {};
	let resumeFilesId = {};
	let resumeFilesIsRemove = {};

	if (!resumeData) {
		return {
			resumeFiles,
			resumeFilesIsDefault,
			resumeFilesId,
			resumeFilesIsRemove,
		};
	}

	// Handle array format
	if (Array.isArray(resumeData) && resumeData.length > 0) {
		// Check if any file is set as default
		const hasDefault = hasDefaultFile(resumeData);

		resumeData.forEach((item, index) => {
			if (typeof item === "string") {
				// Simple path string case
				resumeFiles.push({
					resume_file: {
						path: item,
						name: `Resume ${index + 1}`,
						storage_path: item,
						id: null,
					},
					is_default: !hasDefault && index === 0 ? "1" : "0",
				});
				resumeFilesIsDefault[index] = !hasDefault && index === 0 ? "1" : "0";
				resumeFilesId[index] = null;
				resumeFilesIsRemove[index] = "0";
			} else if (typeof item === "object") {
				// Object format case
				const fileId = item.id || null;

				// Normalize is_default value
				let isDefaultValue = "0";

				// Check if current item is set as default
				if (
					item.is_default === true ||
					item.is_default === "true" ||
					item.is_default === 1 ||
					item.is_default === "1"
				) {
					isDefaultValue = "1";
				}
				// If no default and this is first file
				else if (!hasDefault && index === 0) {
					isDefaultValue = "1";
				}

				resumeFiles.push({
					resume_file: {
						path: item.file_path || item[0],
						name: item.file_name || item[1] || `Resume ${index + 1}`,
						storage_path: item.file_path || item[0],
						id: fileId,
						type: item.file_type || "pdf",
					},
					is_default: isDefaultValue,
					id: fileId,
				});
				resumeFilesIsDefault[index] = isDefaultValue;
				resumeFilesId[index] = fileId;
				resumeFilesIsRemove[index] = "0";
			}
		});
	}
	// Handle single string path
	else if (typeof resumeData === "string") {
		resumeFiles.push({
			resume_file: {
				path: resumeData,
				name: "Resume",
				storage_path: resumeData,
				id: null,
			},
			is_default: "1", // Only file is default
		});
		resumeFilesIsDefault[0] = "1";
		resumeFilesId[0] = null;
		resumeFilesIsRemove[0] = "0";
	}
	// Handle single object
	else if (typeof resumeData === "object" && !Array.isArray(resumeData)) {
		const item = resumeData;
		const fileId = item.id || null;

		// Normalize is_default value
		let isDefaultValue = "1"; // Default for single file
		if (
			item.is_default === false ||
			item.is_default === "false" ||
			item.is_default === 0 ||
			item.is_default === "0"
		) {
			isDefaultValue = "0";
		}

		resumeFiles.push({
			resume_file: {
				path: item.file_path || "",
				name: item.file_name || "Resume",
				storage_path: item.file_path || "",
				id: fileId,
				type: item.file_type || "pdf",
			},
			is_default: isDefaultValue,
			id: fileId,
		});
		resumeFilesIsDefault[0] = isDefaultValue;
		resumeFilesId[0] = fileId;
		resumeFilesIsRemove[0] = "0";
	}

	return {
		resumeFiles,
		resumeFilesIsDefault,
		resumeFilesId,
		resumeFilesIsRemove,
	};
};

/**
 * Ensure one file is set as default in resume files metadata
 * @param {Object} resumeFilesIsDefault - The is_default metadata object
 * @param {Object} resumeFilesIsRemove - The is_remove metadata object
 * @param {Array} resumeFiles - Optional - The actual resume file objects array
 * @returns {Object} - Updated is_default metadata object
 */
export const ensureOneDefaultFile = (
	resumeFilesIsDefault,
	resumeFilesIsRemove,
	resumeFiles = null
) => {
	if (!resumeFilesIsDefault || Object.keys(resumeFilesIsDefault).length === 0) {
		return {};
	}

	// Check if we have any default file (that isn't marked for removal)
	const hasDefault = Object.entries(resumeFilesIsDefault).some(
		([idx, value]) => value === "1" && resumeFilesIsRemove?.[idx] !== "1"
	);

	// First, ensure removed files are not marked as default
	const updatedIsDefault = { ...resumeFilesIsDefault };
	Object.entries(resumeFilesIsRemove || {}).forEach(([idx, isRemove]) => {
		if (isRemove === "1" && updatedIsDefault[idx] === "1") {
			// Clear default flag for removed files
			updatedIsDefault[idx] = "0";
		}
	});

	// If no default file is found, set the first non-removed file as default
	if (!hasDefault) {
		// Find the first non-removed file index
		const firstNonRemovedIndex = Object.keys(resumeFilesIsDefault).find(
			(idx) => resumeFilesIsRemove?.[idx] !== "1"
		);

		if (firstNonRemovedIndex) {
			// Set all to "0" first
			Object.keys(updatedIsDefault).forEach((key) => {
				updatedIsDefault[key] = "0";
			});

			// Set the first non-removed as default
			updatedIsDefault[firstNonRemovedIndex] = "1";

			// If resume files array is provided, update the file object too
			if (resumeFiles && Array.isArray(resumeFiles)) {
				resumeFiles.forEach((file, idx) => {
					if (file) {
						// Set all files to non-default first
						file.is_default = "0";

						// Set the first non-removed file as default
						if (idx.toString() === firstNonRemovedIndex) {
							file.is_default = "1";
						}
					}
				});
			}

			return updatedIsDefault;
		}
	}

	return updatedIsDefault;
};

/**
 * Process form values with resume_files data for API submission
 * @param {Object} values - Form values with resume_files data
 * @returns {Object} - Processed values ready for API submission
 */
export const processResumeFilesForSubmission = (values) => {
	if (!values.resume_files || values.resume_files.length === 0) {
		return values;
	}

	// Create temporary object for API submission
	const processedValues = { ...values };

	// Remove metadata fields
	delete processedValues.resume_files_is_default;
	delete processedValues.resume_files_id;
	delete processedValues.resume_files_is_remove;

	// Remove original resume_files array
	delete processedValues.resume_files;

	// First, ensure is_default is not set for any removed files
	// Find all non-removed files
	const nonRemovedIndices = getNonRemovedIndices(
		values.resume_files,
		values.resume_files_is_remove
	);
	values.resume_files.forEach((file, index) => {
		if (
			values.resume_files_is_remove?.[index] === "1" &&
			values.resume_files_is_default?.[index] === "1"
		) {
			values.resume_files_is_default[index] = "0";
		}
	});

	// If there are non-removed files but no default is set among them,
	// set the first non-removed file as default
	let hasDefaultNonRemovedFile = false;
	for (const idx of nonRemovedIndices) {
		if (values.resume_files_is_default?.[idx] === "1") {
			hasDefaultNonRemovedFile = true;
			break;
		}
	}

	// Set first non-removed file as default if no non-removed file is set as default
	if (nonRemovedIndices.length > 0 && !hasDefaultNonRemovedFile) {
		values.resume_files_is_default[nonRemovedIndices[0]] = "1";
	}

	// Process files and metadata
	values.resume_files.forEach((file, index) => {
		// Handle our special _isFile wrapper object if needed
		let actualFile = file;
		if (file && file._isFile && file._file instanceof File) {
			actualFile = file._file;
		}

		// Extract file ID
		const fileId = values.resume_files_id?.[index] || extractResumeFileId(file);

		// Add file with proper structure
		processedValues[`resume_files[${index}][file]`] = actualFile;

		// Add is_default - normalize to "0"/"1" string
		let isDefaultValue = values.resume_files_is_default?.[index] || "0";
		isDefaultValue = normalizeDefaultValue(isDefaultValue);

		// Ensure removed files are never marked as default
		const isRemove = values.resume_files_is_remove?.[index] === "1";
		if (isRemove) {
			isDefaultValue = "0"; // Force removed files to be non-default
		}

		processedValues[`resume_files[${index}][is_default]`] = isDefaultValue;

		// Add ID
		processedValues[`resume_files[${index}][id]`] = fileId;

		// Add is_remove
		processedValues[`resume_files[${index}][is_remove]`] =
			values.resume_files_is_remove?.[index] || "0";
	});

	// Final check - ensure at least one non-removed file is set as default
	let hasDefaultFile = false;
	let firstNonRemovedIndex = null;

	// Check if there is a default file that is not removed
	for (let i = 0; i < values.resume_files.length; i++) {
		const isRemove = processedValues[`resume_files[${i}][is_remove]`] === "1";

		if (!isRemove) {
			if (firstNonRemovedIndex === null) {
				firstNonRemovedIndex = i;
			}

			const isDefault =
				processedValues[`resume_files[${i}][is_default]`] === "1";
			if (isDefault) {
				hasDefaultFile = true;
			}
		}
	}

	// If no default file and there is a non-removed file, set the first one as default
	if (!hasDefaultFile && firstNonRemovedIndex !== null) {
		processedValues[`resume_files[${firstNonRemovedIndex}][is_default]`] = "1";
	}

	return processedValues;
};
